import { InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Space, Tabs, Tooltip } from 'antd';
import { fetchAllCustomers } from 'api/customers';
import Layout from 'components/Layout';
import withGuard from 'components/withGuard';
import { AdminContext } from 'constants/contexts';
import { PERMISSIONS } from 'constants/permissions';
import React, {
  useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router';
import { captureException } from 'utils/errors';
import AddCustomerModal from './AddCustomerModal';
import Companies from './Companies';
import { TABS } from './constants';
import Table from './Table';

const TOP_LEVEL_TABS = [
  { key: 'customers', label: 'Customers', children: null },
  { key: 'companies', label: 'Companies', children: null },
];

function Customers() {
  const [search, setSearch] = useState('');
  const [companiesSearch, setCompaniesSearch] = useState('');
  const [addModalOpen, setAddModalOpen] = useState(false);
  const resetFetchRef = useRef(false);
  const [dataVersion, setDataVersion] = useState(1);
  const [activeTab, setActiveTab] = useState(TABS[0]);
  const [activeTopTab, setActiveTopTab] = useState(TOP_LEVEL_TABS[0].key);
  const { organizationId } = useContext(AdminContext);
  const location = useLocation();
  const navigate = useNavigate();

  const handleRefetch = useCallback(() => setDataVersion((v) => v + 1), []);
  const handleResetFetch = useCallback(() => {
    resetFetchRef.current = true;
  }, []);

  useEffect(() => {
    handleRefetch();
  }, [handleRefetch]);

  useEffect(() => {
    if (location.state?.openAddCustomer) {
      setActiveTopTab('customers');
      setAddModalOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const handleChangeTab = (value) => {
    handleResetFetch();
    const currentTab = TABS[value - 1];
    setActiveTab(currentTab);
  };

  const handleStatusName = () => {
    if (activeTab?.id === TABS[0]?.id) {
      return '';
    }
    return activeTab.label.toLocaleLowerCase();
  };

  const fetchCustomers = useCallback(
    (pagination, filter, sorter) => {
      const page = resetFetchRef.current ? 1 : pagination.current;
      const queries = {
        search,
        page,
        page_size: pagination.pageSize,
      };
      if (sorter?.order && sorter?.columnKey) {
        queries.ordering = `${sorter?.order === 'descend' ? '-' : ''}${sorter?.columnKey}`;
      }
      if (activeTab?.id !== TABS[0]?.id) {
        queries.status = handleStatusName();
      }

      resetFetchRef.current = false;

      return fetchAllCustomers(queries)
        .then(({ data: { data: { results, count } } }) => ({
          data: results,
          total: count,
          page,
        }))
        .catch((e) => {
          captureException(e);
          throw e;
        });
    },
    [
      search,
      activeTab,
      dataVersion,
      organizationId,
    ],
  );

  const handleSearchChange = (e) => {
    handleResetFetch();
    setSearch(e.target.value);
  };

  const handleCompaniesSearchChange = (e) => {
    handleResetFetch();
    setCompaniesSearch(e.target.value);
  };

  const statusTabItems = useMemo(
    () => TABS.map((tab) => ({
      key: tab.key,
      label: (
        <Space size={4}>
          {tab.label}
          <Tooltip title={tab.tooltip}>
            <InfoCircleOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />
          </Tooltip>
        </Space>
      ),
      children: null,
    })),
    [],
  );

  return (
    <Layout header={(
      <Layout.Header
        title="Customer Info"
        onSearch={activeTopTab === 'customers' ? handleSearchChange : handleCompaniesSearchChange}
        searchPlaceholder={activeTopTab === 'customers' ? 'Search contacts' : 'Search companies'}
        searchValue={activeTopTab === 'customers' ? search : companiesSearch}
        extra={activeTopTab === 'customers' ? [
          <Button
            key="add-customer"
            type="default"
            icon={<PlusOutlined />}
            onClick={() => setAddModalOpen(true)}
          >
            Add Customer
          </Button>,
        ] : undefined}
      />
    )}
    >
      <Tabs
        activeKey={activeTopTab}
        items={TOP_LEVEL_TABS}
        onChange={setActiveTopTab}
      />
      {activeTopTab === 'customers' && (
        <>
          <Tabs items={statusTabItems} defaultActiveKey={TABS[0].key} onChange={handleChangeTab} />
          <Table fetchCustomers={fetchCustomers} />
          <AddCustomerModal
            open={addModalOpen}
            onClose={() => setAddModalOpen(false)}
            onSuccess={() => { setAddModalOpen(false); handleRefetch(); }}
          />
        </>
      )}
      {activeTopTab === 'companies' && (
        <Companies search={companiesSearch} resetFetchRef={resetFetchRef} />
      )}
    </Layout>
  );
}

export default withGuard(Customers, [PERMISSIONS.CUSTOMERS], { redirect: true });
